import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid
} from '@material-ui/core';
import {
    NavigateBefore as PreviousIcon,
    NavigateNext as NextIcon,
    Save as SaveIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import Swal from 'sweetalert2';
import useChallengeStore from '../../store/challengeStore';
import { useCreateCmsChallenge } from '../../hooks/useCreateCmsChallenge';
import Page1GeneralInfo from './Page1GeneralInfo';
import Page2StepsManagement from './Page2StepsManagement';
import Page3ChallengeOrdering from './Page3ChallengeOrdering';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
    },
    stepper: {
        backgroundColor: 'transparent',
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        '& .MuiStepLabel-label': {
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        }
    },
    content: {
        marginTop: 0,
        marginBottom: theme.spacing(2)
    },
    navigation: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: '#fff',
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[2]
    },
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(1)
    },
    publishContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2)
    },
    orangeButton: {
        backgroundColor: '#ff9800',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#e68900'
        },
        '&:focus': {
            outline: 'none'
        }
    },
    statusSelect: {
        minWidth: 200
    }
}));

const ChallengeBuilder = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { id } = useParams();
    const createChallengeMutation = useCreateCmsChallenge();

    const {
        currentPage,
        nextPage,
        previousPage,
        isDraft,
        togglePublish,
        getChallengeData,
        updateChallengeInfo
    } = useChallengeStore();

    const steps = ['General Information', 'Steps Management', 'Challenge Ordering'];

    const handleNextPage = () => {
        // Save before moving to next page
        const data = getChallengeData();
        const currentChallengeId = useChallengeStore.getState().challengeId;

        createChallengeMutation.mutate({ data, challengeId: currentChallengeId }, {
            onSuccess: (response) => {
                const challengeKey = response?.challenge?.key || response?.data?.challenge?.key;

                if (challengeKey && !currentChallengeId) {
                    updateChallengeInfo('challengeId', challengeKey);
                    
                    // Update URL if we just created the challenge
                    if (!id) {
                        navigate(`/challenge-creation/edit/${challengeKey}`, { replace: true });
                    }
                }

                // Move to next page after successful save
                nextPage();
                window.scrollTo(0, 0);
            },
            onError: (error) => {
                // Set isDraft to true when save fails
                updateChallengeInfo('isDraft', true);

                Swal.fire({
                    type: 'error',
                    title: 'Errore nel salvataggio',
                    text: error?.message || 'Si è verificato un errore durante il salvataggio',
                    showConfirmButton: true
                });
            }
        });
    };

    const handlePreviousPage = () => {
        previousPage();
        window.scrollTo(0, 0);
    };

    const handleSave = () => {
        const data = getChallengeData();
        const currentChallengeId = useChallengeStore.getState().challengeId;
        
        console.log('Saving data:', data);
        console.log('Challenge ID:', currentChallengeId);

        createChallengeMutation.mutate({ data, challengeId: currentChallengeId }, {
            onSuccess: (response) => {
                console.log('Save response:', response);

                // La response ha la struttura: { data: { challenge: { key: "..." } } }
                const challengeKey = response?.challenge?.key || response?.data?.challenge?.key;

                if (challengeKey) {
                    updateChallengeInfo('challengeId', challengeKey);
                    console.log('Challenge ID saved:', challengeKey);

                    // Se siamo in modalità "new", naviga alla modalità edit con l'ID appena creato
                    if (!id) {
                        navigate(`/challenge-creation/edit/${challengeKey}`, { replace: true });
                    }
                }

                Swal.fire({
                    type: 'success',
                    title: 'Challenge salvata',
                    showConfirmButton: false,
                    timer: 1500
                });
            },
            onError: (error) => {
                // Set isDraft to true when save fails
                updateChallengeInfo('isDraft', true);

                Swal.fire({
                    type: 'error',
                    title: 'Errore nel salvataggio',
                    text: error?.message || 'Si è verificato un errore durante il salvataggio',
                    showConfirmButton: true
                });
                console.error('Error saving challenge:', error);
            }
        });
    };

    const renderPage = () => {
        switch (currentPage) {
            case 1:
                return <Page1GeneralInfo />;
            case 2:
                return <Page2StepsManagement />;
            case 3:
                return <Page3ChallengeOrdering />;
            default:
                return <Page1GeneralInfo />;
        }
    };

    return (
        <Box className={classes.root}>
            <Stepper activeStep={currentPage - 1} className={classes.stepper}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box className={classes.content}>
                {renderPage()}
            </Box>

            <Box className={classes.navigation}>
                <Grid container spacing={2}>
                    {/* Row 1: Back and Next buttons */}
                    <Grid item xs={6} md={3}>
                        <Button
                            variant="outlined"
                            startIcon={<PreviousIcon />}
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            style={{ borderColor: '#ff9800', color: '#ff9800', outline: 'none' }}
                            fullWidth
                        >
                            Back
                        </Button>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Button
                            variant="outlined"
                            endIcon={<NextIcon />}
                            onClick={handleNextPage}
                            disabled={currentPage === 3}
                            style={{ borderColor: '#ff9800', color: '#ff9800', outline: 'none' }}
                            fullWidth
                        >
                            Next
                        </Button>
                    </Grid>

                    {/* Row 2 (mobile) / Right side (desktop): Status */}
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={isDraft}
                                onChange={() => togglePublish()}
                                label="Status"
                            >
                                <MenuItem value={true}>Draft</MenuItem>
                                <MenuItem value={false}>Published</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Row 3 (mobile) / Right side (desktop): Save button */}
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            className={classes.orangeButton}
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={createChallengeMutation.isPending}
                            size="large"
                            fullWidth
                        >
                            {createChallengeMutation.isPending ? 'Saving...' : 'Save Challenge'}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};

export default ChallengeBuilder;
